import * as Blockly from 'blockly';
import { dictRegistry } from './openfoam.blocks.js';
import {
  createBlock,
  selectBlockType,
  connectValueBlock,
  createTupleBlock,
  createBlockSectionBlock,
  createMacroBlock,
  createCodestreamBlock,
  createVector3Block,
  createVector3ListBlock,
  createConnectivity4Block,
  createConnectivity4ListBlock,
  createListBlock,
  createNamedListBlock,
  createNamedDictListBlock,
  createFoamFileDictionaryBlock,
  createVector3ValueBlock,
  createConnectivity4ValueBlock,
  createValueListBlock,
  createFunctionCallValueBlock,
  createValueChain,
  createJinjaValueBlock,
  createJinjaIfStatementFromAstInternal,
  createJinjaForStatementFromAst,
  createJinjaSetStatementFromAst,
  createJinjaStatementFromAst,
  createFunctionObjectBlock,
  createBlocksFromStatements,
  getAvailableVariableOptions
} from './openfoam.common.astToBlock.js';

// Block mapping configuration
export const BLOCK_MAPPINGS = {
  dictionary: {
    handler: (node, dictName, workspace, path) =>
      createDictionaryBlock(node, dictName, workspace, path),
  },
  keyValue: {
    handler: (node, dictName, workspace, path) =>
      createEntryBlock(node, dictName, workspace, path),
  },
  tuple: {
    handler: (node, dictName, workspace, path) =>
      createTupleBlock(node, workspace, path),
  },
  blockSection: {
    handler: (node, dictName, workspace, path) =>
      createBlockSectionBlock(node, workspace, path),
  },
  vector3: {
    handler: (node, dictName, workspace, path) =>
      createVector3Block(node, workspace, path, dictName),
  },
  vector3list: {
    handler: (node, dictName, workspace, path) =>
      createVector3ListBlock(node, workspace, path, dictName),
  },
  connectivity4: {
    handler: (node, dictName, workspace, path) =>
      createConnectivity4Block(node, workspace, path),
  },
  connectivity4list: {
    handler: (node, dictName, workspace, path) =>
      createConnectivity4ListBlock(node, workspace, path),
  },
  list: {
    handler: (node, dictName, workspace, path) =>
      createListBlock(node, workspace, path),
  },
  namedStringList: {
    handler: (node, dictName, workspace, path) =>
      createNamedListBlock(node, workspace, path),
  },
  namedDictList: {
    handler: (node, dictName, workspace, path) =>
      createNamedDictListBlock(node, dictName, workspace, path),
  },
  macro: {
    handler: (node, dictName, workspace, path) =>
      createMacroBlock(node, workspace, path),
  },
  codestream: {
    handler: (node, dictName, workspace, path) =>
      createCodestreamBlock(node, workspace, path),
  },
  functionObject: {
    handler: (node, dictName, workspace, path) =>
      createFunctionObjectBlock(node, dictName, workspace, path),
  },
  foamFileDict: {
    handler: (node, dictName, workspace, path) =>
      createFoamFileDictionaryBlock(node, dictName, workspace, path),
  },
  jinja_value: {
    handler: (node, dictName, workspace, path) =>
      createJinjaValueBlock(node, workspace, getAvailableVariableOptions(workspace), path),
  },
  jinja_expression: {
    handler: (node, dictName, workspace, path) =>
      createJinjaValueBlock(node, workspace, getAvailableVariableOptions(workspace), path),
  },
  substitution: {
    handler: (node, dictName, workspace, path) => {
      console.log(`Creating substitution statement block at path: ${path}:`, node);
      
      // Create the common substitution statement block
      const block = createBlock(workspace, 'common_substitution_statement', path);
      if (block && block.getField('VARIABLE')) {
        const variableName = node.variableName || node.value?.replace(/^\$/, '') || 'p';
        block.setFieldValue(variableName, 'VARIABLE');
      }
      return block;
    },
  },
  jinja_if_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaIfStatementFromAstInternal(node, workspace, false, path), // Not nested by default
  },
  jinja_for_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaForStatementFromAst(node, workspace, path),
  },
  jinja_set_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaSetStatementFromAst(node, workspace, path),
  },
  jinja_block_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaStatementFromAst(node, workspace, path),
  },
  jinja_extends_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaStatementFromAst(node, workspace, path),
  },
  jinja_include_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaStatementFromAst(node, workspace, path),
  },
  jinja_macro_statement: {
    handler: (node, dictName, workspace, path) =>
      createJinjaStatementFromAst(node, workspace, path),
  },
  jinja_comment: {
    handler: (node, dictName, workspace, path) =>
      createJinjaStatementFromAst(node, workspace, path),
  },
  default: {
    handler: (node, dictName, workspace, path) => {
      console.warn(`Unsupported node type: ${node.type} at path: ${path}`);
      const block = createBlock(workspace, 'common_value_string', path);
      if (block) {
      block.setFieldValue(JSON.stringify(node), 'VALUE');
      }
      return block;
    },
  },
};

// Value block mapping configuration for createEntryBlock
export const VALUE_MAPPINGS = {
  complex: {
    condition: (value) =>
      value && value.type && [
        'dictionary', 'list', 'namedList', 'namedDictList', 'tuple', 'valueList',
        'blockSection', 'macro', 'codestream', 'functionObject', 'foamFileDict'
      ].includes(value.type),
    handler: (value, dictName, workspace, path) =>
      createBlockFromNodeBFS(value, dictName, workspace, true, path),
  },
  jinja: {
    condition: (value) =>
      value && value.type && ['jinja_value', 'jinja_expression', 'substitution'].includes(value.type),
    handler: (value, dictName, workspace, path) => {
      const variableOptions = workspace.options?.jinjaVariableOptions || [['parameter', 'parameter']];
      return createJinjaValueBlock(value, workspace, variableOptions, path);
    },
  },
  valueList: {
    condition: (value) => value && value.type === 'valueList',
    handler: (value, dictName, workspace, path, entryKey = null) => createValueListBlock(value, workspace, path, dictName, entryKey),
    blockType: 'common_multi_part_value',
  },
  vector3: {
    condition: (value) => value && value.type === 'vector3',
    handler: (value, dictName, workspace, path) => createVector3ValueBlock(value, workspace, path, dictName),
    blockType: 'common_value_vector3',
  },
  connectivity4: {
    condition: (value) => value && value.type === 'connectivity4',
    handler: (value, dictName, workspace, path) => createConnectivity4ValueBlock(value, workspace, path),
    blockType: 'common_value_connectivity4',
  },
  number: {
    condition: (value) => typeof value === 'number',
    handler: (value, dictName, workspace, path, entryKey = null) => {
      const dictModule = dictRegistry.get(dictName);
      const blockType = getSpecializedValueBlockType('number', dictName, entryKey, dictModule) || 'common_value_number';
      console.log(`Using value block type: ${blockType} for number value: ${value} at path: ${path}`);
      const block = createBlock(workspace, blockType, path);
      if (block) block.setFieldValue(String(value), 'VALUE');
      return block;
    },
    blockType: 'common_value_number',
  },
  boolean: {
    condition: (value) => typeof value === 'boolean',
    handler: (value, dictName, workspace, path, entryKey = null) => {
      const dictModule = dictRegistry.get(dictName);
      const blockType = getSpecializedValueBlockType('boolean', dictName, entryKey, dictModule) || 'common_value_boolean';
      console.log(`Using value block type: ${blockType} for boolean value: ${value} at path: ${path}`);
      const block = createBlock(workspace, blockType, path);
      if (block) block.setFieldValue(String(value), 'VALUE');
      return block;
    },
    blockType: 'common_value_boolean',
  },
  string: {
    condition: (value) => true, // Default case
    handler: (value, dictName, workspace, path, entryKey = null) => {
      const dictModule = dictRegistry.get(dictName);
      const blockType = getSpecializedValueBlockType('string', dictName, entryKey, dictModule) || 'common_value_string';
      console.log(`Using value block type: ${blockType} for string value: "${value}" at path: ${path}`);
      const block = createBlock(workspace, blockType, path);
      if (block) block.setFieldValue(String(value), 'VALUE');
      return block;
    },
    blockType: 'common_value_string',
  },
};

/**
 * BFS Node structure for traversal
 */
class BFSNode {
  constructor(astNode, parentBlock, parentInput, dictName, level = 0, path = '', index = 0) {
    this.astNode = astNode;
    this.parentBlock = parentBlock;
    this.parentInput = parentInput; // The input on parent where this should connect
    this.dictName = dictName;
    this.level = level;
    this.path = path;
    this.index = index; // Position among siblings
    this.block = null; // Will be set when block is created
    this.children = []; // Will be populated with child BFSNodes
  }

  /**
   * Check if a node type should be excluded from path generation
   */
  static isJinjaStatementType(nodeType) {
    const jinjaTypes = [
      'jinja_value',
      'jinja_expression', 
      'substitution',
      'jinja_if_statement',
      'jinja_for_statement',
      'jinja_set_statement',
      'jinja_block_statement',
      'jinja_extends_statement',
      'jinja_include_statement',
      'jinja_macro_statement',
      'jinja_comment'
    ];
    return jinjaTypes.includes(nodeType);
  }

  /**
   * Generate path string by concatenating names with underscores, excluding jinja statements
   */
  generatePath(nodeName) {
    // Skip jinja statement types in path generation
    if (BFSNode.isJinjaStatementType(this.astNode.type)) {
      console.log(`  🚫 Skipping jinja type '${this.astNode.type}' in path generation, using parent path: ${this.path}`);
      return this.path; // Return parent path without adding this node
    }

    if (!this.path) {
      return nodeName || 'root';
    }
    return `${this.path}_${nodeName || 'unnamed'}`;
  }

  /**
   * Get node name for path generation, excluding jinja types
   */
  getNodeName() {
    // Don't generate names for jinja statement types since they're excluded from paths
    if (BFSNode.isJinjaStatementType(this.astNode.type)) {
      return null; // This will be handled by generatePath()
    }

    if (this.astNode.key) return this.astNode.key;
    if (this.astNode.name) return this.astNode.name;
    if (this.astNode.type) return this.astNode.type;
    return `node_${this.index}`;
  }

  /**
   * Get effective path for logging purposes (shows actual path that will be used)
   */
  getEffectivePath() {
    if (BFSNode.isJinjaStatementType(this.astNode.type)) {
      return `${this.path} [jinja:${this.astNode.type}]`;
    }
    return this.generatePath(this.getNodeName());
  }
}

/**
 * BFS-based function to connect entries at each level
 */
export function connectEntriesBFS(parentBlock, parentInput, bfsNodes, workspace) {
  try {
    if (!parentInput || !bfsNodes || bfsNodes.length === 0) {
      console.log('No entries to connect or missing parent input');
      return [];
    }

    console.log(`Connecting ${bfsNodes.length} entries at level using BFS`);
    let prevBlock = null;
    const connectedBlocks = [];
    
    // Sort nodes by index to maintain order
    bfsNodes.sort((a, b) => a.index - b.index);
      
    for (const bfsNode of bfsNodes) {
      if (!bfsNode.block) {
        console.warn(`BFS node at path ${bfsNode.path} has no block created`);
        continue;
      }
      
        try {
          if (!prevBlock) {
            // Connect as first entry
          if (parentInput.connection && bfsNode.block.previousConnection) {
            parentInput.connection.connect(bfsNode.block.previousConnection);
            console.log(`Connected first entry at path: ${bfsNode.path}`);
            } else {
            console.warn(`Cannot connect first entry at path ${bfsNode.path} - missing connections`);
            }
        } else if (prevBlock.nextConnection && bfsNode.block.previousConnection) {
            // Chain to previous entry
          prevBlock.nextConnection.connect(bfsNode.block.previousConnection);
          console.log(`Chained entry at path: ${bfsNode.path}`);
          } else {
          console.warn(`Cannot chain entry at path ${bfsNode.path} - missing connections`);
          }
          
        prevBlock = bfsNode.block;
        connectedBlocks.push(bfsNode.block);
        } catch (connectionError) {
        console.error(`Error connecting entry at path ${bfsNode.path}:`, connectionError);
      }
    }
    
    console.log(`Successfully connected ${connectedBlocks.length}/${bfsNodes.length} entries using BFS`);
    return connectedBlocks;
  } catch (error) {
    console.error('Error in connectEntriesBFS:', error);
    return [];
  }
}

/**
 * BFS-based helper function to create a block from any AST node
 */
export function createBlockFromNodeBFS(node, dictName, workspace, isNested = false, path = '') {
  try {
    console.log(`Creating block for node type: ${node.type || 'unknown'} at path: ${path}`);

    // Special handling for nested Jinja if statements
    if (node.type === 'jinja_if_statement' && isNested) {
      return createJinjaIfStatementFromAstInternal(node, workspace, true, path); // Use nested version
    }

    // Get the dictionary module to check for component matching
    const dictModule = dictRegistry.get(dictName);
    if (dictModule && typeof dictModule.findMatchingBlockType === 'function') {
      const matchingResult = dictModule.findMatchingBlockType(node, dictName, path);
      if (matchingResult) {
        console.log(`Using matching block type from ${dictName} at path ${path}:`, matchingResult);
        const blockType = typeof matchingResult === 'string' ? matchingResult : matchingResult.blockType;
        const block = createBlock(workspace, blockType, path);
        
        if (block) {
          // Handle values for key-value pairs
          if (node.type === 'keyValue' && block.getField('KEY')) {
            block.setFieldValue(node.key, 'KEY');
            
            // Create and connect value block if node has a value
            if (node.value !== null && node.value !== undefined) {
              const valuePath = `${path}_value`;
              const valueBlock = createValueFromNode(node.value, dictName, workspace, valuePath, node.key);
                  if (valueBlock) {
                connectValueBlock(block, valueBlock, node.key, path);
              }
            }
          }
          
          return block;
        }
      }
    }

    // Use the dict module's recursive creation if available (for fvSchemes and other enhanced modules)
    if (dictModule && typeof dictModule.createBlockFromComponent === 'function') {
      console.log(`Using enhanced creation from ${dictName} module at path: ${path}`);
      const recursiveBlock = dictModule.createBlockFromComponent(workspace, node, dictName, null, path);
      if (recursiveBlock) {
        console.log(`Successfully created block using enhanced method at path: ${path}`);
        return recursiveBlock;
      }
    }

    // Fall back to default block mapping if no match found
    const mapping = BLOCK_MAPPINGS[node.type] || BLOCK_MAPPINGS.default;
    const block = mapping.handler(node, dictName, workspace, path);
    
    // Handle values for the created block if it's a key-value entry
    if (block && node.type === 'keyValue' && node.value !== null && node.value !== undefined) {
      const valuePath = `${path}_value`;
      const valueBlock = createValueFromNode(node.value, dictName, workspace, valuePath, node.key);
      if (valueBlock) {
        connectValueBlock(block, valueBlock, node.key, path);
      }
    }

    return block;
  } catch (error) {
    console.error(`Error creating block for node type ${node.type || 'unknown'} at path ${path}:`, error);
    return null;
  }
}

/**
 * Create a value block from a node value (primitive or complex object)
 */
function createValueFromNode(value, dictName, workspace, path = '', entryKey = null) {
  try {
    console.log(`Creating value from node at path: ${path}`, typeof value === 'object' ? value : `(${typeof value}) ${value}`);

    // Handle null/undefined values
    if (value === null || value === undefined) {
      console.log(`Null/undefined value at path: ${path}`);
      return null;
    }

    // Handle complex objects with type
    if (typeof value === 'object' && value.type) {
      console.log(`Complex value object with type: ${value.type} at path: ${path}`);
      
      // Handle valueList directly (don't route through BFS)
      if (value.type === 'valueList') {
        console.log(`Processing valueList directly at path: ${path}`);
        const valueListMapping = VALUE_MAPPINGS.valueList;
        return valueListMapping.handler(value, dictName, workspace, path, entryKey);
      }
      
      // Check for Jinja values
      const jinjaMapping = VALUE_MAPPINGS.jinja;
      if (jinjaMapping.condition(value)) {
        console.log(`Processing Jinja value at path: ${path}`);
        return jinjaMapping.handler(value, dictName, workspace, path);
      }
      
      // Check for other specific value types (excluding complex to avoid BFS routing)
      for (const [mappingName, mapping] of Object.entries(VALUE_MAPPINGS)) {
        if (mappingName !== 'complex' && mappingName !== 'jinja' && mappingName !== 'string' && mappingName !== 'valueList' && mapping.condition(value)) {
          console.log(`Processing ${mappingName} value at path: ${path}`);
          // Pass entryKey if the handler supports it
          if (mapping.handler.length >= 5) {
            return mapping.handler(value, dictName, workspace, path, entryKey);
          } else {
            return mapping.handler(value, dictName, workspace, path);
          }
        }
      }
      
      // For other complex types that need BFS (dictionaries, lists, etc.), still use BFS but only for structural types
      const complexMapping = VALUE_MAPPINGS.complex;
      if (complexMapping.condition(value)) {
        // Only allow structural types through BFS, not value types
        if (['dictionary', 'list', 'namedList', 'namedDictList', 'functionObject'].includes(value.type)) {
          console.log(`Processing structural complex value through BFS at path: ${path}`);
          return complexMapping.handler(value, dictName, workspace, path);
        } else {
          console.log(`Skipping BFS for value type: ${value.type}, handling as string at path: ${path}`);
          // Handle as string fallback
          const blockType = getSpecializedValueBlockType('string', dictName, entryKey) || 'common_value_string';
          const block = createBlock(workspace, blockType, path);
          if (block) {
            block.setFieldValue(JSON.stringify(value), 'VALUE');
          }
          return block;
        }
      }
      
      // Fallback for unknown object types
      console.warn(`Unknown object type: ${value.type} at path: ${path}, falling back to string representation`);
      const blockType = getSpecializedValueBlockType('string', dictName, entryKey) || 'common_value_string';
      const block = createBlock(workspace, blockType, path);
      if (block) {
        block.setFieldValue(JSON.stringify(value), 'VALUE');
      }
      return block;
    }

    // Get dictionary module for potential validation and specialized blocks
    const dictModule = dictRegistry.get(dictName);
    
    // Handle primitive types with potential domain-specific validation and specialized blocks
    if (typeof value === 'number') {
      console.log(`Creating number value: ${value} at path: ${path}`);
      let finalValue = value;
      
      // Apply validation if available and entryKey is provided
      if (entryKey && dictModule?.KEYWORD_CONFIGS) {
        const config = dictModule.KEYWORD_CONFIGS.find(cfg => cfg.key === entryKey);
        if (config && config.validator) {
          try {
            finalValue = config.validator(value);
            console.log(`Applied validation for key ${entryKey}: ${value} -> ${finalValue}`);
          } catch (validationError) {
            console.warn(`Validation failed for key ${entryKey}:`, validationError);
          }
        }
      }
      
      // Use VALUE_MAPPINGS for consistency
      const numberMapping = VALUE_MAPPINGS.number;
      return numberMapping.handler(finalValue, dictName, workspace, path, entryKey);
    }

    if (typeof value === 'boolean') {
      console.log(`Creating boolean value: ${value} at path: ${path}`);
      
      // Use VALUE_MAPPINGS for consistency
      const booleanMapping = VALUE_MAPPINGS.boolean;
      return booleanMapping.handler(value, dictName, workspace, path, entryKey);
    }

    // Handle string values with potential domain-specific validation and specialized blocks
    console.log(`Creating string value: ${value} at path: ${path}`);
    let finalValue = value;
    
    // Apply validation if available and entryKey is provided
    if (entryKey && dictModule?.KEYWORD_CONFIGS) {
      const config = dictModule.KEYWORD_CONFIGS.find(cfg => cfg.key === entryKey);
      if (config && config.validator) {
        try {
          finalValue = config.validator(value);
          console.log(`Applied validation for key ${entryKey}: ${value} -> ${finalValue}`);
        } catch (validationError) {
          console.warn(`Validation failed for key ${entryKey}:`, validationError);
        }
      }
    }
    
    // Use VALUE_MAPPINGS for consistency
    const stringMapping = VALUE_MAPPINGS.string;
    return stringMapping.handler(finalValue, dictName, workspace, path, entryKey);

  } catch (error) {
    console.error(`Error creating value from node at path ${path}:`, error);
    return null;
  }
}

/**
 * Get specialized value block type based on dictionary name and entry key
 */
function getSpecializedValueBlockType(valueType, dictName, entryKey = null, dictModule = null) {
  try {
    // If dictionary module provides a method to get value block types, use it
    if (dictModule && typeof dictModule.getValueBlockType === 'function') {
      const specializedType = dictModule.getValueBlockType(valueType, entryKey);
      if (specializedType && Blockly.Blocks[specializedType]) {
        console.log(`Dictionary module provided specialized value block: ${specializedType}`);
        return specializedType;
      }
    }

    // If dictionary module has value block mappings, check them
    if (dictModule && dictModule.VALUE_BLOCK_MAPPINGS) {
      const mapping = dictModule.VALUE_BLOCK_MAPPINGS[entryKey];
      if (mapping && mapping[valueType] && Blockly.Blocks[mapping[valueType]]) {
        console.log(`Found specialized value block from mappings: ${mapping[valueType]}`);
        return mapping[valueType];
      }
    }

    // Check for common dictionary-specific patterns
    const dictPrefix = dictModule?.prefix || dictName;
    const candidateTypes = [];

    if (entryKey) {
      // Try patterns like: dictName_value_entryKey, dictName_entryKey_value
      candidateTypes.push(`${dictPrefix}_value_${entryKey}`);
      candidateTypes.push(`${dictPrefix}_${entryKey}_value`);
      candidateTypes.push(`${dictPrefix}_${entryKey}_${valueType}`);
    }

    // Try general patterns: dictName_value_type
    candidateTypes.push(`${dictPrefix}_value_${valueType}`);
    candidateTypes.push(`${dictName}_value_${valueType}`);

    // Check if any candidate types exist
    for (const candidateType of candidateTypes) {
      if (Blockly.Blocks[candidateType]) {
        console.log(`Found specialized value block: ${candidateType}`);
        return candidateType;
      }
    }

    console.log(`No specialized value block found for ${valueType} in ${dictName}${entryKey ? ` (key: ${entryKey})` : ''}, using common block`);
    return null; // Will fall back to common block
  } catch (error) {
    console.error(`Error getting specialized value block type:`, error);
    return null;
  }
}

/**
 * Main BFS traversal function for creating blocks from tree structure
 */
export function createBlocksUsingBFS(ast, dictName, workspace) {
  try {
    console.log(`\n=== STARTING BFS TRAVERSAL FOR DICTIONARY: ${dictName} ===`);
    console.log('AST:', JSON.stringify(ast, null, 2));

    // Create root block
    let rootBlock;
    const specificRootType = `${dictName}_dict`;
    const rootPath = dictName;

    console.log(`\n--- LEVEL 0: ROOT BLOCK CREATION ---`);
    if (Blockly.Blocks[specificRootType]) {
      console.log(`Creating specific root block: ${specificRootType} at path: ${rootPath}`);
      rootBlock = createBlock(workspace, specificRootType, rootPath);
      } else {
      console.log(`Specific root block ${specificRootType} not found, using common_dict at path: ${rootPath}`);
      rootBlock = createBlock(workspace, 'common_dict', rootPath);
      if (rootBlock && rootBlock.getField('DICT_NAME')) {
        rootBlock.setFieldValue(dictName, 'DICT_NAME');
      }
    }

    if (!rootBlock) {
      console.error('❌ Failed to create root block');
    return null;
  }
    console.log(`✅ Root block created successfully at path: ${rootPath}`);

    // Initialize BFS queue with root level nodes
    const queue = [];
    const levelGroups = new Map(); // Group nodes by level for processing
    const levelStats = new Map(); // Track statistics for each level
    
    // Add root level statements to queue
    if (ast.statements && ast.statements.length > 0) {
      console.log(`\n--- INITIALIZING LEVEL 1 NODES ---`);
      console.log(`Found ${ast.statements.length} root-level statements to process`);
      
      ast.statements.forEach((statement, index) => {
        const bfsNode = new BFSNode(
          statement, 
          rootBlock, 
          rootBlock.getInput('ENTRIES'),
          dictName, 
          1, // Level 1 (root is level 0)
          rootPath,
          index
        );
        bfsNode.path = bfsNode.generatePath(bfsNode.getNodeName());
        queue.push(bfsNode);
        
        console.log(`  📝 Queued node [${index}]: ${statement.type || 'unknown'} -> path: ${bfsNode.getEffectivePath()}`);
        
        // Group by level
        if (!levelGroups.has(1)) {
          levelGroups.set(1, []);
          levelStats.set(1, { total: 0, created: 0, connected: 0, failed: 0 });
        }
        levelGroups.get(1).push(bfsNode);
        levelStats.get(1).total++;
      });
    } else {
      console.log(`\n⚠️  No statements found in AST - only root block will be created`);
    }

    // Process levels using BFS
    let currentLevel = 1;
    while (levelGroups.has(currentLevel)) {
      const currentLevelNodes = levelGroups.get(currentLevel);
      const stats = levelStats.get(currentLevel);
      
      console.log(`\n=== PROCESSING LEVEL ${currentLevel} ===`);
      console.log(`📊 Level ${currentLevel} Statistics:`);
      console.log(`  - Total nodes to process: ${stats.total}`);
      console.log(`  - Node types: ${currentLevelNodes.map(n => n.astNode.type).join(', ')}`);
      
      // Create blocks for all nodes at current level
      console.log(`\n--- BLOCK CREATION PHASE (Level ${currentLevel}) ---`);
      const createdPaths = [];
      const failedPaths = [];
      
      for (const [nodeIndex, bfsNode] of currentLevelNodes.entries()) {
        console.log(`\n🔨 Creating block [${nodeIndex + 1}/${currentLevelNodes.length}] at path: ${bfsNode.getEffectivePath()}`);
        console.log(`  - Node type: ${bfsNode.astNode.type || 'unknown'}`);
        console.log(`  - Parent: ${bfsNode.parentBlock?.type || 'unknown'} (id: ${bfsNode.parentBlock?.id})`);
        console.log(`  - Parent input: ${bfsNode.parentInput?.name || 'unknown'}`);
        
        // Create the block for this node
        bfsNode.block = createBlockFromNodeBFS(
          bfsNode.astNode, 
          bfsNode.dictName, 
          workspace, 
          false, 
          bfsNode.path
        );
        
        if (bfsNode.block) {
          createdPaths.push(bfsNode.getEffectivePath());
          stats.created++;
          console.log(`  ✅ Successfully created block: ${bfsNode.block.type} (id: ${bfsNode.block.id})`);
          
          // Collect child nodes for next level
          const childNodes = getChildNodes(bfsNode.astNode, bfsNode, workspace);
          if (childNodes.length > 0) {
            console.log(`  👶 Found ${childNodes.length} child nodes for next level`);
            
            childNodes.forEach((childBfsNode, childIndex) => {
              queue.push(childBfsNode);
              console.log(`    - Child [${childIndex}]: ${childBfsNode.astNode.type || 'unknown'} -> path: ${childBfsNode.getEffectivePath()}`);
              
              // Group by level
              if (!levelGroups.has(childBfsNode.level)) {
                levelGroups.set(childBfsNode.level, []);
                levelStats.set(childBfsNode.level, { total: 0, created: 0, connected: 0, failed: 0 });
              }
              levelGroups.get(childBfsNode.level).push(childBfsNode);
              levelStats.get(childBfsNode.level).total++;
            });
        } else {
            console.log(`  🍃 No child nodes found (leaf node)`);
          }
        } else {
          failedPaths.push(bfsNode.getEffectivePath());
          stats.failed++;
          console.log(`  ❌ Failed to create block at path: ${bfsNode.getEffectivePath()}`);
        }
      }
      
      // Summary of block creation phase
      console.log(`\n📋 BLOCK CREATION SUMMARY (Level ${currentLevel}):`);
      console.log(`  ✅ Successfully created blocks (${createdPaths.length}):`);
      createdPaths.forEach(path => console.log(`    - ${path}`));
      
      if (failedPaths.length > 0) {
        console.log(`  ❌ Failed to create blocks (${failedPaths.length}):`);
        failedPaths.forEach(path => console.log(`    - ${path}`));
      }
      
      // Connect all blocks at current level to their parents
      console.log(`\n--- CONNECTION PHASE (Level ${currentLevel}) ---`);
      const parentGroups = new Map();
      
      for (const bfsNode of currentLevelNodes) {
        if (bfsNode.block && bfsNode.parentBlock && bfsNode.parentInput) {
          const parentKey = `${bfsNode.parentBlock.id}_${bfsNode.parentInput.name}`;
          if (!parentGroups.has(parentKey)) {
            parentGroups.set(parentKey, {
              parentBlock: bfsNode.parentBlock,
              parentInput: bfsNode.parentInput,
              children: []
            });
          }
          parentGroups.get(parentKey).children.push(bfsNode);
        }
      }
      
      console.log(`🔗 Found ${parentGroups.size} parent groups to connect`);
      
      // Connect each group of siblings
      let connectionIndex = 0;
      const connectedPaths = [];
      
      for (const [parentKey, group] of parentGroups.entries()) {
        connectionIndex++;
        console.log(`\n🔗 Connecting group [${connectionIndex}/${parentGroups.size}]:`);
        console.log(`  - Parent: ${group.parentBlock.type} (id: ${group.parentBlock.id})`);
        console.log(`  - Input: ${group.parentInput.name}`);
        console.log(`  - Children to connect: ${group.children.length}`);
        
        group.children.forEach((child, idx) => {
          console.log(`    [${idx}] ${child.getEffectivePath()} (${child.astNode.type})`);
        });
        
        const connectedBlocks = connectEntriesBFS(group.parentBlock, group.parentInput, group.children, workspace);
        
        connectedBlocks.forEach(block => {
          const bfsNode = group.children.find(n => n.block === block);
          if (bfsNode) {
            connectedPaths.push(bfsNode.getEffectivePath());
            stats.connected++;
          }
        });
        
        console.log(`  ✅ Connected ${connectedBlocks.length}/${group.children.length} blocks in this group`);
      }
      
      // Summary of connection phase
      console.log(`\n🔗 CONNECTION SUMMARY (Level ${currentLevel}):`);
      console.log(`  ✅ Successfully connected blocks (${connectedPaths.length}):`);
      connectedPaths.forEach(path => console.log(`    - ${path}`));
      
      // Level completion summary
      console.log(`\n📊 LEVEL ${currentLevel} COMPLETION SUMMARY:`);
      console.log(`  - Total nodes: ${stats.total}`);
      console.log(`  - Blocks created: ${stats.created}/${stats.total} (${((stats.created/stats.total)*100).toFixed(1)}%)`);
      console.log(`  - Blocks connected: ${stats.connected}/${stats.created} (${stats.created > 0 ? ((stats.connected/stats.created)*100).toFixed(1) : 0}%)`);
      console.log(`  - Failed: ${stats.failed}`);
      
      if (levelGroups.has(currentLevel + 1)) {
        const nextLevelCount = levelGroups.get(currentLevel + 1).length;
        console.log(`  - Child nodes queued for level ${currentLevel + 1}: ${nextLevelCount}`);
      }
      
      currentLevel++;
    }

    // Final summary
    console.log(`\n=== BFS TRAVERSAL COMPLETED ===`);
    console.log(`📊 FINAL STATISTICS:`);
    console.log(`  - Total levels processed: ${levelStats.size}`);
    
    let totalNodes = 0, totalCreated = 0, totalConnected = 0, totalFailed = 0;
    
    for (const [level, stats] of levelStats.entries()) {
      totalNodes += stats.total;
      totalCreated += stats.created;
      totalConnected += stats.connected;
      totalFailed += stats.failed;
      
      console.log(`  - Level ${level}: ${stats.created}/${stats.total} created, ${stats.connected} connected, ${stats.failed} failed`);
    }
    
    console.log(`📈 OVERALL TOTALS:`);
    console.log(`  - Total nodes processed: ${totalNodes}`);
    console.log(`  - Total blocks created: ${totalCreated}/${totalNodes} (${totalNodes > 0 ? ((totalCreated/totalNodes)*100).toFixed(1) : 0}%)`);
    console.log(`  - Total blocks connected: ${totalConnected}/${totalCreated} (${totalCreated > 0 ? ((totalConnected/totalCreated)*100).toFixed(1) : 0}%)`);
    console.log(`  - Total failures: ${totalFailed}`);
    
    console.log(`\n🎉 BFS traversal completed successfully for dictionary: ${dictName}`);
    console.log(`✅ Root block created with ${totalCreated} descendant blocks`);
    
    return rootBlock;
  } catch (error) {
    console.error('💥 FATAL ERROR in BFS traversal:', error);
    console.error('Stack trace:', error.stack);
    return null;
  }
}

/**
 * Enhanced child node extraction that handles structural nodes but NOT value nodes
 */
function getChildNodes(astNode, parentBfsNode, workspace) {
  const children = [];
  let childIndex = 0;

  try {
    // Handle dictionary entries
    if (astNode.entries && Array.isArray(astNode.entries)) {
      console.log(`🔍 Processing ${astNode.entries.length} entries for node at path: ${parentBfsNode.getEffectivePath()}`);
      console.log(`Entry types:`, astNode.entries.map(e => e.type || 'unknown'));
      
      const entriesInput = parentBfsNode.block.getInput('ENTRIES') || 
                          parentBfsNode.block.getInput('SCHEMES') ||
                          parentBfsNode.block.getInput('ITEMS') ||
                          parentBfsNode.block.getInput('COEFFS') ||
                          parentBfsNode.block.getInput('SOLVERS') ||
                          parentBfsNode.block.getInput('PARAMETERS');
      
      if (entriesInput) {
        console.log(`✅ Found entries input: ${entriesInput.name}`);
      } else {
        console.log(`❌ No entries input found for block type: ${parentBfsNode.block.type}`);
      }
      
      if (entriesInput) {
        astNode.entries.forEach((entry, index) => {
          // Generate child path, but let the BFS node handle jinja exclusions
          const childNodeName = entry.key || entry.name || entry.type || `entry_${index}`;
          
          const childBfsNode = new BFSNode(
            entry,
            parentBfsNode.block,
            entriesInput,
            parentBfsNode.dictName,
            parentBfsNode.level + 1,
            parentBfsNode.path, // Pass parent path, let generatePath handle exclusions
            childIndex++
          );
          
          // Generate the actual path using the BFS node's logic
          childBfsNode.path = childBfsNode.generatePath(childNodeName);
          
          console.log(`  ➕ Adding child [${index}]: ${entry.type} (${entry.name || entry.key || 'unnamed'}) -> path: ${childBfsNode.path}`);
          children.push(childBfsNode);
          
          // NOTE: Value blocks are NO LONGER added to BFS queue
          // Values will be created immediately during entry block creation
          // This prevents multiple value block creation and simplifies the flow
        });
      }
    }

    // Handle list items
    if (astNode.items && Array.isArray(astNode.items)) {
      const itemsInput = parentBfsNode.block.getInput('ITEMS') || 
                        parentBfsNode.block.getInput('ENTRIES');
      
      if (itemsInput) {
        astNode.items.forEach((item, index) => {
          // Only add complex items that need their own blocks (NOT simple values)
          if (typeof item === 'object' && item !== null && item.type) {
            // Only add structural nodes, not value nodes
            if (item.type === 'dictionary' || item.type === 'list' || 
                item.type.startsWith('jinja_') || item.type === 'functionObject' ||
                item.type === 'namedDictList' || item.type === 'namedStringList') {
              
              const childBfsNode = new BFSNode(
                item,
                parentBfsNode.block,
                itemsInput,
                parentBfsNode.dictName,
                parentBfsNode.level + 1,
                parentBfsNode.path, // Pass parent path
                childIndex++
              );
              
              // Generate path with jinja exclusion logic
              childBfsNode.path = childBfsNode.generatePath(`item_${index}`);
              
              children.push(childBfsNode);
            }
            // Skip valueList and other value types - they'll be handled in createValueFromNode
          }
        });
      }
    }

    // Handle other specific node types with children
    if (astNode.vectors && Array.isArray(astNode.vectors)) {
      const vectorsInput = parentBfsNode.block.getInput('VECTORS');
      if (vectorsInput) {
        astNode.vectors.forEach((vector, index) => {
          const childBfsNode = new BFSNode(
            vector,
            parentBfsNode.block,
            vectorsInput,
            parentBfsNode.dictName,
            parentBfsNode.level + 1,
            parentBfsNode.path,
            childIndex++
          );
          
          childBfsNode.path = childBfsNode.generatePath(`vector_${index}`);
          children.push(childBfsNode);
        });
      }
    }

    if (astNode.connections && Array.isArray(astNode.connections)) {
      const connectionsInput = parentBfsNode.block.getInput('CONNECTIONS');
      if (connectionsInput) {
        astNode.connections.forEach((conn, index) => {
          const childBfsNode = new BFSNode(
            conn,
            parentBfsNode.block,
            connectionsInput,
            parentBfsNode.dictName,
            parentBfsNode.level + 1,
            parentBfsNode.path,
            childIndex++
          );
          
          childBfsNode.path = childBfsNode.generatePath(`connection_${index}`);
          children.push(childBfsNode);
        });
      }
    }

    // Handle Jinja statement bodies
    if (astNode.branches && Array.isArray(astNode.branches)) {
      let elifIndex = 0; // Separate counter for elif branches
      
      astNode.branches.forEach((branch, branchIndex) => {
        if (branch.body && Array.isArray(branch.body)) {
          let inputName;
          
          if (branch.type === 'if') {
            inputName = 'IF_BODY';
          } else if (branch.type === 'elif') {
            inputName = `ELIF_BODY${elifIndex}`; // Use elif-specific index
            elifIndex++; // Increment elif counter
          } else if (branch.type === 'else') {
            inputName = 'ELSE_BODY';
          }
          
          const bodyInput = parentBfsNode.block.getInput(inputName);
          if (bodyInput) {
            branch.body.forEach((stmt, index) => {
              const childBfsNode = new BFSNode(
                stmt,
                parentBfsNode.block,
                bodyInput,
                parentBfsNode.dictName,
                parentBfsNode.level + 1,
                parentBfsNode.path,
                childIndex++
              );
              
              // Don't add jinja branch type to path - let generatePath handle exclusions
              const childNodeName = stmt.key || stmt.name || stmt.type || `stmt_${index}`;
              childBfsNode.path = childBfsNode.generatePath(childNodeName);
              children.push(childBfsNode);
            });
          } else {
            console.warn(`Input ${inputName} not found for ${branch.type} branch at path: ${parentBfsNode.getEffectivePath()}`);
          }
        }
      });
    }

    if (astNode.body && Array.isArray(astNode.body)) {
      const bodyInput = parentBfsNode.block.getInput('BODY');
      if (bodyInput) {
        astNode.body.forEach((stmt, index) => {
          const childBfsNode = new BFSNode(
            stmt,
            parentBfsNode.block,
            bodyInput,
            parentBfsNode.dictName,
            parentBfsNode.level + 1,
            parentBfsNode.path,
            childIndex++
          );
          
          // Don't add "body" to path for jinja nodes - let generatePath handle exclusions
          const childNodeName = stmt.key || stmt.name || stmt.type || `stmt_${index}`;
          childBfsNode.path = childBfsNode.generatePath(childNodeName);
          children.push(childBfsNode);
        });
      }
    }

  } catch (error) {
    console.error(`Error extracting child nodes for path ${parentBfsNode.getEffectivePath()}:`, error);
  }

  console.log(`Extracted ${children.length} child nodes for path: ${parentBfsNode.getEffectivePath()}`);
  return children;
}

/**
 * Create dictionary block (domain-specific function)
 */
function createDictionaryBlock(dict, dictName, workspace, path = '') {
  try {
    const dictNameDisplay = dict.name || 'unnamed';
    console.log(`Creating dictionary block: ${dictNameDisplay} at path: ${path}`);

    const specificBlockType = dict.name ? `${dictName}_${dict.name}` : `${dictName}_dict`;
    const blockType = selectBlockType([specificBlockType, 'common_dict', 'common_entry']);
    
    if (!blockType) {
      console.error(`No valid block type found for dictionary at path: ${path}`);
      return null;
    }

    let block;
    if (blockType === 'common_entry') {
      console.log(`Falling back to common_entry for ${dictNameDisplay} at path: ${path}`);
      block = createBlock(workspace, 'common_entry', path);
      if (!block) return null;
      
      block.setFieldValue(dictNameDisplay, 'KEY');
      
      const valueBlock = createBlock(workspace, 'common_value_string', `${path}_value`);
      if (valueBlock) {
        valueBlock.setFieldValue('{dictionary}', 'VALUE');
        const valueInput = block.getInput('VALUE');
        if (valueInput) {
          valueInput.connection.connect(valueBlock.outputConnection);
        }
      }
    } else {
      console.log(`Using block type: ${blockType} for ${dictNameDisplay} at path: ${path}`);
      block = createBlock(workspace, blockType, path);
      if (!block) return null;
      
      if (blockType === 'common_dict' && block.getField('DICT_NAME')) {
        block.setFieldValue(dictNameDisplay, 'DICT_NAME');
      }
    }

    // Note: Entries will be connected via BFS traversal
    return block;
  } catch (error) {
    console.error(`Error creating dictionary block for ${dict.name || 'unnamed'} at path ${path}:`, error);
    return null;
  }
}

/**
 * Create key-value entry block (domain-specific function)
 */
function createEntryBlock(entry, dictName, workspace, path = '') {
  try {
    console.log(`Creating entry block for key: ${entry.key} at path: ${path}`);

    const dictModule = dictRegistry.get(dictName);
    const dictPrefix = dictModule?.prefix || dictName;
    const specificBlockType = `${dictPrefix}_${entry.key}`;
    const blockType = selectBlockType([specificBlockType, 'common_entry']);

    if (!blockType) {
      console.error(`No valid block type found for entry ${entry.key} at path: ${path}`);
      return null;
    }

    const block = createBlock(workspace, blockType, path);
  if (!block) return null;
  
    if (blockType === 'common_entry') {
      block.setFieldValue(entry.key, 'KEY');
    }
    console.log(`Using block type: ${blockType} for entry ${entry.key} at path: ${path}`);

    return block;
  } catch (error) {
    console.error(`Error creating entry block for ${entry.key} at path ${path}:`, error);
    return null;
  }
}

/**
 * Create Blockly blocks from AST using BFS
 */
export function createBlocksFromAst(ast, dictName, workspace) {
  console.log(`Using BFS approach to create blocks from AST for dictionary: ${dictName}`);
  return createBlocksUsingBFS(ast, dictName, workspace);
}

/**
 * Create Jinja if statement blocks from AST (exported for external use)
 */
export function createJinjaIfStatementFromAst(astNode, workspace, isNested = false, path = '') {
  return createJinjaIfStatementFromAstInternal(astNode, workspace, isNested, path);
} 